import type { components } from '@connected/api-client/api.generated';
import type { UseQueryOptions } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type FileQueryKey = readonly unknown[];
export type FileMetaData = components['schemas']['ListFileQueryResultFile'];

export type FileQueryOptions<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>
    = UseQueryOptions<TData, TError, TData, TQueryKey> & {
        queryFn: NonNullable<UseQueryOptions<TData, TError, TData, TQueryKey>['queryFn']>;
    };

export type UseFileOptions<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey> = {
    queryOptions: FileQueryOptions<TData, TError, TQueryKey>;
    metaData?: FileMetaData;
    enabled?: boolean;
};

export function useFile<TData extends Blob | null, TError = unknown, TQueryKey extends FileQueryKey = FileQueryKey>({
    queryOptions,
    metaData,
    enabled = true,
}: UseFileOptions<TData, TError, TQueryKey>) {
    const { data, ...queryState } = useQuery({ ...queryOptions, gcTime: 0, staleTime: 0, enabled });

    const fileData = useMemo<File | Blob | null>(() => {
        if (!enabled)
            return null;

        // Early return if the data is not a blob
        if (!data)
            return null;

        if (!(data instanceof Blob) || data.size === 0)
            return null;

        if (!metaData)
            return data;

        return new File([data], metaData?.Name ?? 'unknown', {
            type: metaData.ContentType ?? undefined,
            lastModified: metaData.LastUpdateDate ? new Date(metaData.LastUpdateDate).getTime() : undefined,
        });
    }, [data, enabled, metaData]);

    if (!data && enabled) {
        return {
            ...queryState,
            data: null,
            isError: true,
            error: new Error('File not found'),
        } as const;
    }

    if (!enabled) {
        return {
            ...queryState,
            data: null,
        } as const;
    }

    return {
        ...queryState,
        data: fileData,
    } as const;
}
